import { css } from '@emotion/react';
import { useFocusRing } from '@react-aria/focus';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import React, { CSSProperties, forwardRef, InputHTMLAttributes, useRef, useState, useMemo } from 'react';
import { semantic_colors } from '../../foundation';
import { buildCSSWithLength, size, getZIndex } from '../../styles';
import { AriaFocusProps } from '../../types/common';
import { Chip } from '../chip';
import { getDraggableInputChipSizeStyle } from '../chip/styles';
import { IconWarning } from '../icons/generated';
import {
  getInputFontStyle,
  getInputHeightBySize,
  getInputStatusIconSize,
  input_base_css,
  input_default_style,
  input_disabled_style,
  input_error_style,
  input_focus_style,
  input_status_color_css,
  input_status_icon_css,
  input_wrapper_base_css,
} from './styles';
import { InputSize, InputStatus } from './types';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import type { Active, UniqueIdentifier } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';
import { createUniqueIdentifier } from '../../utils/createUniqueIdentifier';

export interface ChipInputValue {
  id: UniqueIdentifier;
  value: string;
  deletable?: boolean;
  disabled?: boolean;
}

export interface ChipInputProps
  extends AriaFocusProps,
    Omit<InputHTMLAttributes<HTMLInputElement>, 'width' | 'size' | 'value' | 'onChange'> {
  value?: ChipInputValue[];
  disabled?: boolean;
  placeholder?: string;
  /** @default 'medium' */
  size?: InputSize;
  /**
   * `error` 이외에 상태는 별도로 UI상 표시되지 않습니다.
   * @default 'info'
   * */
  status?: InputStatus;
  /**
   * 칩 목록은 한 줄로 제한하거나 개행되도록 할 수 있습니다.
   *
   * - `nowrap`: 칩이 인풋의 너비를 초과하면 수평으로 스크롤됩니다.
   * - `wrap`: 칩이 인풋의 너비를 초과하면 수직으로 스크롤됩니다.
   * @default 'nowrap'
   * */
  wrap?: 'wrap' | 'nowrap';
  width?: number;
  /**
   * `wrap` prop이 `"wrap"`인 경우 최대 높이를 지정할 수 있습니다.
   * 최대 높이를 초과하면 수직으로 스크롤됩니다.
   * `maxHeight` prop이 지정되지 않으면 `size` prop에 따라 기본 높이가 적용됩니다.(small: 66px, medium: 80px, large: 90px)
   * infinite 적용시 value 만큼 높이가 증가합니다.
   */
  maxHeight?: number | 'infinite';
  /**
   * draggable이 true인 경우 칩을 드래그하여 재정렬할 수 있습니다.
   * @default false
   */
  draggable?: boolean;
  className?: string;
  style?: CSSProperties;
  /**
   * DragOverlay의 z-index를 지정합니다.
   * @default getZIndex('draggable')
   */
  zIndex?: number;
  onChange?: (value: ChipInputValue[]) => void;
}

export const getChipInputPaddingBySize = (size: InputSize) => {
  const padding: { [key in InputSize]: string } = {
    xsmall: '2px 8px',
    small: '4px 10px',
    medium: '5px 12px',
    large: '7px 16px',
  };
  return padding[size];
};

const getChipInputSizeStyle = (size: InputSize = 'large') => {
  return css`
    gap: 4px;
    ${buildCSSWithLength('min-height', getInputHeightBySize(size))}
    ${buildCSSWithLength('padding', getChipInputPaddingBySize(size))}
  `;
};

const getHeightStyleBySize = (maxHeight?: number | string, size?: InputSize) => {
  if (typeof maxHeight === 'string') {
    return;
  }
  if (typeof maxHeight === 'number') {
    return css`
      ${getChipInputSizeStyle(size)}
      max-height: ${maxHeight}px;
    `;
  }
  switch (size) {
    case 'xsmall':
      return css`
        ${getChipInputSizeStyle(size)}
        max-height: 66px;
      `;
    case 'small':
      return css`
        ${getChipInputSizeStyle(size)}
        max-height: 66px;
      `;
    case 'medium':
      return css`
        ${getChipInputSizeStyle(size)}
        max-height: 80px;
      `;
    case 'large':
      return css`
        ${getChipInputSizeStyle(size)}
        max-height: 90px;
      `;
    default:
      return '';
  }
};

const SortableChip = ({
  chip,
  draggable,
  size = 'medium',
  disabled,
  onDelete,
}: { chip: ChipInputValue; onDelete: () => void } & Pick<ChipInputProps, 'draggable' | 'size' | 'disabled'>) => {
  const able_to_drag = draggable && !disabled;
  const { isOver, activeIndex, overIndex, attributes, isDragging, listeners, setNodeRef, setActivatorNodeRef } =
    useSortable({
      id: chip.id,
      disabled: !able_to_drag,
    });
  const renderDndCursor = (show: boolean) => {
    return (
      <div
        css={css`
          flex-shrink: 0;
          display: ${show ? 'block' : 'none'};
          width: 1px;
          height: 26px;
          background-color: ${semantic_colors.accent.primary};
        `}
      />
    );
  };
  return (
    <>
      {renderDndCursor(isOver && overIndex <= activeIndex)}
      <Chip
        ref={setNodeRef}
        size={size}
        disabled={chip.disabled || disabled || isDragging}
        deletable={chip.deletable}
        onDelete={onDelete}
        css={
          able_to_drag &&
          css`
            ${getDraggableInputChipSizeStyle(size, chip.deletable)}
            cursor: grab;

            &:hover {
              background-color: #f7f7f7;
              transition: background-color 0.1s linear;
            }
          `
        }
      >
        <div className='grab-handle' {...attributes} {...listeners} ref={setActivatorNodeRef}>
          {chip.value}
        </div>
      </Chip>
      {renderDndCursor(isOver && overIndex > activeIndex)}
    </>
  );
};

/**
 * 입력된 텍스트가 있다면 아래의 경우에 Chip이 생성 됩니다.
 * - 엔터키를 입력
 * - 쉼표(,)를 입력
 * - blur 이벤트가 발생
 * - 붙여넣기시 쉼표(,)로 구분된 값들이 칩으로 추가
 *
 * 아래의 경우에 Chip이 삭제 됩니다.
 * - Chip의 삭제 버튼을 클릭
 * - 입력된 텍스트가 없는 상태에서 백스페이스를 누르면 마지막 Chip이 삭제됩니다.
 */
export const ChipInput = forwardRef<HTMLInputElement, ChipInputProps>(
  (
    {
      value,
      size: sizeProp = 'medium',
      status = 'info',
      className,
      style,
      disabled,
      width,
      placeholder,
      wrap = 'nowrap',
      maxHeight,
      draggable = false,
      unstableFocusVisible,
      zIndex = getZIndex('draggable'),
      onChange,
      onBlur,
      onPaste,
      onKeyDown,
      onKeyUp,
      ...props
    },
    ref,
  ) => {
    const container_ref = useRef<HTMLDivElement>(null);
    const [input_value, setInputValue] = useState('');
    const [active_chip, setActiveChip] = useState<Active | null>(null);
    const { isFocusVisible, focusProps } = useFocusRing({ within: true, isTextInput: true });

    const activeItem = useMemo(() => value?.find((chip) => chip.id === active_chip?.id), [active_chip, value]);
    const sensors = useSensors(useSensor(PointerSensor));

    const status_style = {
      ...input_default_style,
      ...((unstableFocusVisible ?? isFocusVisible) && input_focus_style),
      ...(disabled && input_disabled_style),
      ...(status && status === 'error' && input_error_style),
    };
    const input_color = disabled
      ? value?.length
        ? semantic_colors.content.secondary
        : semantic_colors.content.disabled
      : semantic_colors.content.primary;
    const statusIconSize = getInputStatusIconSize(sizeProp);

    const wrap_style =
      wrap === 'nowrap'
        ? css`
            flex-wrap: ${wrap};
            overflow-x: auto;
            overflow-y: hidden;
          `
        : css`
            flex-wrap: ${wrap};
            overflow-x: hidden;
            overflow-y: auto;
            ${getHeightStyleBySize(maxHeight, sizeProp)}
          `;

    const renderToInputStatus = () => {
      if (status === 'error') {
        return (
          <span css={input_status_icon_css}>
            <IconWarning size={statusIconSize} color={input_status_color_css.error} />
          </span>
        );
      }
      return null;
    };

    const addChip = () => {
      const text = input_value.replace(/,/g, '').trim();

      setInputValue('');

      if (!text) return;

      onChange?.([...(value || []), { id: createUniqueIdentifier(), value: text, deletable: true, disabled: false }]);

      setTimeout(() => {
        if (wrap === 'nowrap') {
          if (typeof width === 'number' && container_ref.current && container_ref.current.scrollWidth > width) {
            container_ref.current.scrollLeft = container_ref.current.scrollWidth;
          }
        } else if (wrap === 'wrap') {
          if (container_ref.current && container_ref.current.scrollHeight > container_ref.current.clientHeight) {
            container_ref.current.scrollTop = container_ref.current.scrollHeight;
          }
        }
      }, 50);
    };

    const handleInputKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (['Enter', ','].includes(event.key) && !event.nativeEvent.isComposing && input_value) {
        event.preventDefault();
        addChip();
      }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      event.preventDefault();

      const pasted_text = event.clipboardData.getData('text');
      const chip_list = pasted_text
        .split(',')
        .filter((text) => Boolean(text.trim()))
        .map((text) => ({
          id: createUniqueIdentifier(),
          value: text.trim(),
          deletable: true,
          disabled: false,
        }));

      if (!chip_list.length) return;

      onChange?.([...(value || []), ...chip_list]);
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Backspace' && !input_value && value?.length && value[value.length - 1].deletable) {
        event.preventDefault();
        onChange?.(value?.slice(0, -1) || []);
      }
    };

    return (
      <div
        ref={container_ref}
        className={classNames(['pds-input-wrapper', className])}
        css={[
          input_wrapper_base_css,
          getChipInputSizeStyle(sizeProp),
          wrap_style,
          status_style,
          size({ width }),
          { ...style },
        ]}
        {...focusProps}
      >
        <DndContext
          sensors={sensors}
          onDragStart={({ active }) => {
            if (draggable) {
              setActiveChip(active);
            }
          }}
          onDragEnd={({ active, over }) => {
            if (draggable) {
              if (over && active.id !== over?.id) {
                const activeIndex = value?.findIndex(({ id }) => id === active.id);
                const overIndex = value?.findIndex(({ id }) => id === over.id);
                // @ts-expect-error: TS2345
                onChange?.(arrayMove(value, activeIndex, overIndex));
              }
              setActiveChip(null);
            }
          }}
          onDragCancel={() => {
            if (draggable) {
              setActiveChip(null);
            }
          }}
        >
          {/* @ts-expect-error: TS2322 */}
          <SortableContext items={value}>
            {value?.map((chip, index) => {
              return (
                <SortableChip
                  key={index}
                  chip={chip}
                  draggable={draggable}
                  disabled={disabled}
                  size={sizeProp}
                  onDelete={() => {
                    onChange?.(value?.filter((_, i) => i !== index) || []);
                  }}
                />
              );
            })}
            {createPortal(
              // iframe에서 렌더링할 때는 body에 렌더링되지 않으므로
              // body에 렌더링하지 않으면 드래그 오버레이가 iframe 내부에 렌더링되어
              // createPortal로 body에 렌더링함
              <DragOverlay style={{ marginLeft: 5 }} zIndex={zIndex}>
                {activeItem ? (
                  <Chip
                    size={sizeProp}
                    disabled={disabled || activeItem.disabled}
                    deletable={activeItem.deletable}
                    css={css`
                      cursor: grabbing;
                      box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.1);
                      transition: opacity 0.1s linear;
                    `}
                  >
                    {activeItem.value}
                  </Chip>
                ) : null}
              </DragOverlay>,
              document.body,
            )}
          </SortableContext>
        </DndContext>

        <input
          className='pds-input'
          css={[
            input_base_css,
            getInputFontStyle(sizeProp),
            css`
              flex: 1 1 0;
              min-width: 40px;
              color: ${input_color};
              ${disabled && ` -webkit-text-fill-color: ${input_color}`};
            `,
          ]}
          value={input_value}
          disabled={disabled}
          placeholder={!value?.length ? placeholder : undefined}
          ref={ref}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyUp={(e) => {
            handleInputKeyUp(e);
            onKeyUp?.(e);
          }}
          onKeyDown={(e) => {
            handleInputKeyDown(e);
            onKeyDown?.(e);
          }}
          onPaste={(e) => {
            handlePaste(e);
            onPaste?.(e);
          }}
          onBlur={(e) => {
            addChip();
            onBlur?.(e);
          }}
          {...props}
        />
        {renderToInputStatus()}
      </div>
    );
  },
);
